import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import * as Tabs from '@radix-ui/react-tabs';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AppShell } from '../../components/AppShell';
import { Card, CardBody, CardHeader } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import { useWorkspace } from '../../hooks/useWorkspace';
import { HankoStamp } from '../../components/ui/HankoStamp';
import { HankoGenerator } from '../../components/HankoGenerator';
import { LanguagePicker } from '../../components/LanguagePicker';
import { EntitiesCard } from '../../components/EntitiesCard';
import { PostingAccountsCard } from '../../components/PostingAccountsCard';
import { MemberPermissionsCard } from '../../components/MemberPermissionsCard';
import { SampleDataCard } from '../../components/SampleDataCard';
import { useToast } from '../../components/ui/Toast';
import { useCan } from '../../hooks/useCan';
import { api } from '../../lib/api';

type NotificationChannel = 'email' | 'slack' | 'teams' | 'line_works' | 'chatwork';
interface NotificationSetting { channel: NotificationChannel; config: Record<string, unknown>; }

interface SsoConnection {
  id: string;
  protocol: 'saml' | 'oidc';
  idp_entity_id: string | null;
  domain: string | null;
  enabled: boolean;
  created_at: string;
}

export function SettingsPage() {
  const { wsId } = useParams<{ wsId: string }>();
  const { t } = useTranslation();
  const { canMutate } = useCan();
  const { data: user } = useCurrentUser();
  const { data: ws } = useWorkspace(wsId);
  const qc = useQueryClient();

  // Returning from Stripe (?billing=success|cancelled) lands the user on
  // the Billing tab so the BillingReturnBanner is visible. Captured once;
  // BillingReturnBanner strips the param on mount.
  const [initialTab] = useState<(typeof TAB_KEYS)[number]>(() => {
    if (typeof window === 'undefined') return 'general';
    const b = new URLSearchParams(window.location.search).get('billing');
    return b === 'success' || b === 'cancelled' ? 'billing' : 'general';
  });

  const notifSettings = useQuery<NotificationSetting[]>({
    queryKey: ['notif-settings', wsId],
    enabled: !!wsId,
    queryFn: async () => (await api.get(`/workspaces/${wsId}/notifications/settings`)).data,
  });

  const sso = useQuery<SsoConnection[]>({
    queryKey: ['sso-connections'],
    enabled: !!user?.companyId,
    queryFn: async () => (await api.get('/sso/connections')).data,
  });

  const generateScim = useMutation({
    mutationFn: async () => (await api.post<{ token: string }>('/sso/scim-token')).data,
  });

  const channels: NotificationChannel[] = ['email', 'slack', 'teams', 'line_works', 'chatwork'];

  const updateNotif = useMutation({
    mutationFn: async (input: { channel: NotificationChannel; value: string }) => {
      const cfg = input.channel === 'email' ? { email: input.value }
        : input.channel === 'chatwork' ? { token: input.value }
        : { webhookUrl: input.value };
      return (await api.post(`/workspaces/${wsId}/notifications/settings`, {
        channel: input.channel,
        config: cfg,
      })).data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notif-settings', wsId] }),
  });

  const testNotif = useMutation({
    mutationFn: async (channel: NotificationChannel) =>
      (await api.post(`/workspaces/${wsId}/notifications/test`, { channel })).data,
  });

  const [newSsoProtocol, setNewSsoProtocol] = useState<'saml' | 'oidc'>('saml');
  const [newSsoDomain, setNewSsoDomain] = useState('');
  const [newSsoMetadata, setNewSsoMetadata] = useState('');

  const configureSso = useMutation({
    mutationFn: async () =>
      (await api.post('/sso/configure', {
        protocol: newSsoProtocol,
        domain: newSsoDomain || undefined,
        idpMetadataXml: newSsoMetadata || undefined,
      })).data,
    onSuccess: () => {
      setNewSsoDomain('');
      setNewSsoMetadata('');
      qc.invalidateQueries({ queryKey: ['sso-connections'] });
    },
  });

  const notifValueFor = (channel: NotificationChannel): string => {
    const setting = notifSettings.data?.find((s) => s.channel === channel);
    if (!setting) return '';
    const cfg = setting.config as Record<string, string>;
    return cfg.email ?? cfg.webhookUrl ?? cfg.token ?? '';
  };

  return (
    <AppShell>
      <div className="px-8 py-7 max-w-4xl">
        <h1 className="display text-2xl font-semibold mb-6">{t('settings.title')}</h1>

        <Tabs.Root defaultValue={initialTab}>
          <Tabs.List
            className="flex flex-wrap gap-1 mb-6 border-b border-ink-200 dark:border-ink-800"
            aria-label={t('settings.title')}
          >
            {TAB_KEYS.map((key) => (
              <Tabs.Trigger
                key={key}
                value={key}
                className="px-3 py-2 -mb-px text-sm font-medium text-ink-500 border-b-2 border-transparent hover:text-ink-900 dark:hover:text-ink-100 data-[state=active]:text-accent-700 dark:data-[state=active]:text-accent-300 data-[state=active]:border-accent-500 transition-colors"
              >
                {t(`settings.tabs.${key}`)}
              </Tabs.Trigger>
            ))}
          </Tabs.List>

          <Tabs.Content value="general" className="focus:outline-none">
            <Card className="mb-5">
              <CardHeader>
                <div className="font-medium">{t('settings.profile')}</div>
              </CardHeader>
              <CardBody>
                <div className="flex items-center gap-4">
                  {user?.pictureUrl ? (
                    <img src={user.pictureUrl} alt="" className="h-14 w-14 rounded-full" />
                  ) : (
                    <div className="h-14 w-14 rounded-full bg-accent-500 text-white flex items-center justify-center font-semibold">
                      {user?.name[0]?.toUpperCase()}
                    </div>
                  )}
                  <div>
                    <div className="font-medium">{user?.name}</div>
                    <div className="text-sm text-ink-500">{user?.email}</div>
                  </div>
                </div>
                <div className="mt-5">
                  <div className="text-xs uppercase tracking-wider text-ink-500 mb-2">{t('languagePicker.label')}</div>
                  <LanguagePicker />
                </div>
              </CardBody>
            </Card>

            <Card className="mb-5">
              <CardHeader>
                <div className="font-medium">{t('settings.hanko')}</div>
              </CardHeader>
              <CardBody className="space-y-4">
                <div className="flex items-center gap-4">
                  {user?.hankoImageUrl ? (
                    <img src={user.hankoImageUrl} alt={`Hanko: ${user.hankoSealText ?? ''}`} width={64} height={64} />
                  ) : (
                    <HankoStamp text={user?.hankoSealText ?? (user?.name?.split(' ')[0] ?? 'K')} size={64} />
                  )}
                  <div className="text-sm text-ink-500">{t('settings.hankoHint')}</div>
                </div>
                <HankoGenerator
                  initialText={user?.hankoSealText ?? user?.name?.split(' ')[0] ?? ''}
                  onSaved={() => qc.invalidateQueries({ queryKey: ['currentUser'] })}
                />
              </CardBody>
            </Card>

            <JpPrefsCard />

            <Card className="mb-5">
              <CardHeader>
                <div className="font-medium">{t('settings.workspace')}</div>
              </CardHeader>
              <CardBody className="space-y-3">
                <Row label={t('settings.rows.name')} value={ws?.name ?? '—'} />
                <Row label={t('settings.rows.baseCurrency')} value={ws?.base_currency ?? '—'} />
                <Row label={t('settings.rows.inboxEmail')} value={ws ? `${ws.inbox_alias}@in.plyez.com` : '—'} mono />
              </CardBody>
            </Card>

            <SampleDataCard />
          </Tabs.Content>

          <Tabs.Content value="notifications" className="focus:outline-none">
            <Card className="mb-5">
              <CardHeader>
                <div className="font-medium">{t('notifications.title')}</div>
              </CardHeader>
              <CardBody className="space-y-3">
                {channels.map((c) => (
                  <div key={c} className="flex items-center gap-3">
                    <div className="w-32 text-sm text-ink-500">{t(`notifications.channels.${c}`)}</div>
                    <input
                      defaultValue={notifValueFor(c)}
                      disabled={!canMutate}
                      title={canMutate ? undefined : t('common.viewerReadOnly')}
                      onBlur={(e) => {
                        if (e.target.value !== notifValueFor(c)) {
                          updateNotif.mutate({ channel: c, value: e.target.value });
                        }
                      }}
                      className="flex-1 h-9 px-3 rounded-lg border border-ink-300 dark:border-ink-700 bg-white dark:bg-ink-900 text-sm disabled:opacity-50"
                    />
                    <button
                      onClick={() => testNotif.mutate(c)}
                      disabled={testNotif.isPending || !canMutate}
                      title={canMutate ? undefined : t('common.viewerReadOnly')}
                      className="px-3 py-1.5 rounded-lg border border-ink-300 dark:border-ink-700 text-xs font-medium disabled:opacity-50"
                    >
                      {t('common.send')}
                    </button>
                  </div>
                ))}
              </CardBody>
            </Card>
          </Tabs.Content>

          <Tabs.Content value="accounting" className="focus:outline-none">
            <PostingAccountsCard />
            <EntitiesCard />
          </Tabs.Content>

          <Tabs.Content value="team" className="focus:outline-none">
            <MemberPermissionsCard />
          </Tabs.Content>

          <Tabs.Content value="billing" className="focus:outline-none">
            <Card className="mb-5">
              <CardHeader>
                <div className="font-medium">{t('settings.plan', { defaultValue: 'Plan' })}</div>
              </CardHeader>
              <CardBody>
                <BillingReturnBanner />
                <PlanPicker companyId={user?.companyId ?? null} />
              </CardBody>
            </Card>
          </Tabs.Content>

          <Tabs.Content value="integrations" className="focus:outline-none">
            <Card>
              <CardHeader>
                <div className="font-medium">{t('sso.title')}</div>
              </CardHeader>
              <CardBody className="space-y-4">
                <div>
                  <div className="text-xs uppercase tracking-wider text-ink-500 mb-2">{t('sso.connections')}</div>
                  {sso.isLoading ? (
                    <div className="text-sm text-ink-500">{t('common.loading')}</div>
                  ) : !sso.data || sso.data.length === 0 ? (
                    <div className="text-sm text-ink-500">{t('common.none')}</div>
                  ) : (
                    <ul className="text-sm space-y-1">
                      {sso.data.map((c) => (
                        <li key={c.id} className="flex items-center justify-between">
                          <span className="font-mono uppercase">{c.protocol}</span>
                          <span className="text-ink-500">{c.domain ?? '—'}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                {canMutate && (
                <div className="space-y-3 pt-3 border-t border-ink-200 dark:border-ink-800">
                  <div className="grid md:grid-cols-2 gap-3">
                    <label className="block">
                      <div className="text-xs uppercase tracking-wider text-ink-500 mb-1">{t('sso.protocol')}</div>
                      <select
                        value={newSsoProtocol}
                        onChange={(e) => setNewSsoProtocol(e.target.value as 'saml' | 'oidc')}
                        className="w-full h-10 px-3 rounded-lg border border-ink-300 dark:border-ink-700 bg-white dark:bg-ink-900"
                      >
                        <option value="saml">SAML</option>
                        <option value="oidc">OIDC</option>
                      </select>
                    </label>
                    <label className="block">
                      <div className="text-xs uppercase tracking-wider text-ink-500 mb-1">{t('sso.domain')}</div>
                      <input
                        value={newSsoDomain}
                        onChange={(e) => setNewSsoDomain(e.target.value)}
                        placeholder="example.com"
                        className="w-full h-10 px-3 rounded-lg border border-ink-300 dark:border-ink-700 bg-white dark:bg-ink-900"
                      />
                    </label>
                  </div>
                  <label className="block">
                    <div className="text-xs uppercase tracking-wider text-ink-500 mb-1">{t('sso.metadataXml')}</div>
                    <textarea
                      value={newSsoMetadata}
                      onChange={(e) => setNewSsoMetadata(e.target.value)}
                      rows={4}
                      placeholder="<EntityDescriptor ...>"
                      className="w-full px-3 py-2 rounded-lg border border-ink-300 dark:border-ink-700 bg-white dark:bg-ink-900 text-xs font-mono"
                    />
                  </label>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => configureSso.mutate()}
                      disabled={configureSso.isPending}
                      className="px-3 py-1.5 rounded-lg bg-accent-600 text-white text-xs font-medium hover:bg-accent-700 disabled:opacity-50"
                    >
                      {t('sso.configure')}
                    </button>
                    <button
                      onClick={() => generateScim.mutate()}
                      disabled={generateScim.isPending}
                      className="px-3 py-1.5 rounded-lg border border-ink-300 dark:border-ink-700 text-xs font-medium"
                    >
                      {t('sso.scimToken')}
                    </button>
                  </div>
                  {generateScim.data && (
                    <div className="text-xs">
                      <div className="text-warn-600 mb-1">{t('sso.scimTokenWarning')}</div>
                      <div className="font-mono break-all p-2 bg-ink-50 dark:bg-ink-900 rounded">{generateScim.data.token}</div>
                    </div>
                  )}
                </div>
                )}
              </CardBody>
            </Card>
          </Tabs.Content>
        </Tabs.Root>
      </div>
    </AppShell>
  );
}

const TAB_KEYS = ['general', 'notifications', 'accounting', 'team', 'billing', 'integrations'] as const;

interface CompanyShape { id: string; plan: 'free' | 'growth' | 'global' | 'enterprise'; name?: string }

interface BillingMe {
  hasStripeCustomer: boolean;
  interval: 'monthly' | 'yearly' | null;
  current_period_end: string | null;
  status: string | null;
}

// Per-tier feature keys (resolved via i18n `pricing.feat.*`) shared with
// the public PricingPage so both surfaces stay in lock-step. Only the
// first FEATURE_PREVIEW_COUNT show by default; the rest sit behind a
// "Show all features" expander.
const PLAN_FEATURE_KEYS: Record<'free' | 'growth' | 'global' | 'enterprise', string[]> = {
  free: ['free5Seats', 'oneEntity', 'invoices50', 'aiLight', 'pintSend'],
  growth: ['unlimitedInvoices', 'oneEntity', 'jpComplianceAll', 'docOcrMatch', 'arCollections', 'pintSendReceive', 'aiCap25', 'slackTeamsLineworks'],
  global: ['unlimitedEntities', 'multiCurrencyFx', 'crossBorderRails', 'vendorPortal', 'reporting1099', 'aiCap50', 'peppolPriority'],
  enterprise: ['sso', 'auditLog', 'jpRegistryFeed', 'soc2', 'sla', 'customAiRetention', 'regionPinning'],
};
const FEATURE_PREVIEW_COUNT = 6;

/**
 * Surfaces the Stripe-return status (`?billing=success` / `?billing=cancelled`)
 * as a dismissable inline banner. Also strips the query params so a
 * refresh doesn't re-trigger the banner.
 */
function BillingReturnBanner() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [billing, setBilling] = useState<'success' | 'cancelled' | null>(null);
  const qc = useQueryClient();
  useEffect(() => {
    const status = searchParams.get('billing');
    if (status === 'success' || status === 'cancelled') {
      setBilling(status);
      if (status === 'success') {
        qc.invalidateQueries({ queryKey: ['company'] });
        qc.invalidateQueries({ queryKey: ['billing-me'] });
      }
      const next = new URLSearchParams(searchParams);
      next.delete('billing');
      next.delete('session_id');
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  if (!billing) return null;
  return billing === 'success' ? (
    <div role="status" className="mb-3 rounded-md border border-emerald-200 bg-emerald-50 dark:bg-emerald-950/30 dark:border-emerald-800 px-3 py-2 text-[13px] text-emerald-800 dark:text-emerald-200 flex items-center justify-between gap-2">
      <span>Subscription updated. It can take a moment for the new plan to reflect — refresh if needed.</span>
      <button type="button" onClick={() => setBilling(null)} className="text-emerald-700 dark:text-emerald-300 text-xs font-semibold">×</button>
    </div>
  ) : (
    <div role="status" className="mb-3 rounded-md border border-ink-200 dark:border-ink-700 bg-ink-50 dark:bg-ink-800/50 px-3 py-2 text-[13px] text-ink-700 dark:text-ink-300 flex items-center justify-between gap-2">
      <span>Checkout cancelled. No changes were made.</span>
      <button type="button" onClick={() => setBilling(null)} className="text-ink-500 text-xs font-semibold">×</button>
    </div>
  );
}

/**
 * In-app plan picker. Shows Free + 3 paid tiers side-by-side with a
 * monthly/yearly toggle, current-plan badge, and Subscribe/Switch
 * per tier. Calls /api/billing/checkout with the chosen tier +
 * interval; Stripe Checkout handles the rest. Free is informational.
 */
function PlanPicker({ companyId }: { companyId: string | null }) {
  const { t, i18n } = useTranslation();
  const [interval, setInterval] = useState<'monthly' | 'yearly'>('monthly');
  const [intervalTouched, setIntervalTouched] = useState(false);
  const [pending, setPending] = useState<'growth' | 'global' | 'enterprise' | null>(null);
  const [error, setError] = useState<string | null>(null);

  const companyQ = useQuery<CompanyShape>({
    queryKey: ['company', companyId],
    enabled: !!companyId,
    queryFn: async () => (await api.get(`/companies/${companyId}`)).data,
  });
  const meBilling = useQuery<BillingMe>({
    queryKey: ['billing-me', companyId],
    enabled: !!companyId,
    queryFn: async () => (await api.get('/billing/me')).data,
  });
  const currentPlan = companyQ.data?.plan ?? 'free';
  const canManage = meBilling.data?.hasStripeCustomer === true;
  const currentInterval = meBilling.data?.interval ?? null;
  const periodEnd = meBilling.data?.current_period_end ?? null;
  const subStatus = meBilling.data?.status ?? null;

  // Preselect the toggle to the user's CURRENT billing cycle once it
  // resolves — but never override a deliberate user toggle.
  useEffect(() => {
    if (!intervalTouched && currentInterval) setInterval(currentInterval);
  }, [currentInterval, intervalTouched]);

  const renewalLabel = (() => {
    if (!periodEnd) return null;
    const d = new Date(periodEnd);
    if (Number.isNaN(d.getTime())) return null;
    const date = new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' }).format(d);
    // cancel_at_period_end / canceled → the access ENDS; otherwise it renews.
    const ending = subStatus === 'canceled' || subStatus === 'unpaid' || subStatus === 'incomplete_expired';
    return ending
      ? t('billing.ends', { date, defaultValue: 'Ends {{date}}' })
      : t('billing.renews', { date, defaultValue: 'Renews {{date}}' });
  })();

  async function openPortal() {
    setError(null);
    try {
      const r = await api.post<{ url: string }>('/billing/portal', {
        returnUrl: `${window.location.origin}/app/settings`,
      });
      if (r.data.url) window.location.href = r.data.url;
    } catch (err) {
      setError((err as Error).message);
    }
  }

  // Live Stripe-sourced plan catalogue. Never hardcode prices here —
  // a Stripe Dashboard edit would silently desync the picker from
  // what Checkout actually charges.
  type PlanEntry = { code: 'free' | 'growth' | 'global' | 'enterprise'; monthlyUnitAmountCents: number; yearlyUnitAmountCents: number; currency: string };
  const plansQ = useQuery<PlanEntry[]>({
    queryKey: ['billing-plans'],
    queryFn: async () => (await api.get<{ plans: PlanEntry[] }>('/billing/plans')).data.plans,
    staleTime: 5 * 60 * 1000,
  });
  const plans = plansQ.data ?? [];
  const priceFor = (code: 'free' | 'growth' | 'global' | 'enterprise'): number => {
    const p = plans.find((x) => x.code === code);
    if (!p) return 0;
    const cents = interval === 'monthly' ? p.monthlyUnitAmountCents : p.yearlyUnitAmountCents;
    return interval === 'yearly' ? Math.round(cents / 12) / 100 : cents / 100;
  };
  const currency = (plans[0]?.currency ?? 'usd').toUpperCase();
  const fmt = (n: number) => new Intl.NumberFormat(undefined, { style: 'currency', currency, maximumFractionDigits: 0 }).format(n);

  const tiers: Array<{
    code: 'free' | 'growth' | 'global' | 'enterprise';
    name: string;
    featureKeys: string[];
  }> = [
    { code: 'free',       name: t('pricing.freeTier',       { defaultValue: 'Free' }),       featureKeys: PLAN_FEATURE_KEYS.free },
    { code: 'growth',     name: t('pricing.growthTier',     { defaultValue: 'Growth' }),     featureKeys: PLAN_FEATURE_KEYS.growth },
    { code: 'global',     name: t('pricing.globalTier',     { defaultValue: 'Global' }),     featureKeys: PLAN_FEATURE_KEYS.global },
    { code: 'enterprise', name: t('pricing.enterpriseTier', { defaultValue: 'Enterprise' }), featureKeys: PLAN_FEATURE_KEYS.enterprise },
  ];

  async function subscribe(plan: 'growth' | 'global' | 'enterprise', tierInterval: 'monthly' | 'yearly') {
    if (!companyId) return;
    setError(null);
    setPending(plan);
    try {
      // Existing paid subscribers route plan/cycle CHANGES through the
      // Stripe Customer Portal, which handles proration + cycle switch
      // correctly. Checkout is reserved for the FIRST subscribe from Free.
      if (canManage) {
        const r = await api.post<{ url: string }>('/billing/portal', {
          returnUrl: `${window.location.origin}/app/settings`,
        });
        if (r.data.url) window.location.href = r.data.url;
        return;
      }
      const r = await api.post<{ url: string }>('/billing/checkout', {
        plan,
        interval: tierInterval,
        seats: 1,
        successUrl: `${window.location.origin}/app?billing=success&session_id={CHECKOUT_SESSION_ID}`,
        cancelUrl: `${window.location.origin}/app/settings?billing=cancelled`,
      });
      if (r.data.url) window.location.href = r.data.url;
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      setError(status === 503 ? t('settings.billing_unavailable', { defaultValue: 'Billing is not configured yet.' }) : (err as Error).message);
      setPending(null);
    }
  }

  return (
    <>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <p className="text-sm text-ink-500">{t('settings.planIntro', { defaultValue: 'All tiers include AI inference (no markup). Switch any time; Stripe handles proration.' })}</p>
        <div className="flex items-center gap-2 flex-wrap">
          {canManage && (
            <button
              type="button"
              onClick={openPortal}
              className="text-xs px-3 py-1.5 rounded-md border border-ink-300 dark:border-ink-700 text-ink-700 dark:text-ink-300 hover:bg-ink-100 dark:hover:bg-ink-800 font-semibold"
            >
              {t('settings.manageSubscription', { defaultValue: 'Manage subscription' })}
            </button>
          )}
          <div className="inline-flex rounded-md border border-ink-200 dark:border-ink-700 p-0.5 bg-ink-50 dark:bg-ink-800/50 text-xs font-semibold">
            {(['monthly', 'yearly'] as const).map((iv) => (
              <button
                key={iv}
                type="button"
                onClick={() => { setIntervalTouched(true); setInterval(iv); }}
                className={'px-3 py-1.5 rounded transition ' + (interval === iv ? 'bg-white dark:bg-ink-900 text-ink-900 dark:text-ink-100 shadow-sm' : 'text-ink-500 hover:text-ink-900')}
              >
                {iv === 'monthly' ? t('pricing.monthly', { defaultValue: 'Monthly' }) : t('pricing.yearlyDiscount', { defaultValue: 'Yearly · −17%' })}
              </button>
            ))}
          </div>
        </div>
      </div>

      {renewalLabel && (
        <p className="mt-2 text-xs text-ink-500">
          <span className="inline-block rounded bg-accent-50 dark:bg-accent-950/30 text-accent-700 dark:text-accent-400 px-2 py-0.5 font-semibold">
            {currentInterval === 'yearly' ? t('billing.cycleYearly', { defaultValue: 'Yearly' }) : t('billing.cycleMonthly', { defaultValue: 'Monthly' })}
          </span>{' '}
          {renewalLabel}
        </p>
      )}

      {error && (
        <p role="alert" className="mt-3 rounded-md border border-amber-200 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800 px-3 py-2 text-[13px] text-amber-800 dark:text-amber-200">
          {error}
        </p>
      )}

      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        {tiers.map((tier) => (
          <TierCard
            key={tier.code}
            tier={tier}
            interval={interval}
            currentPlan={currentPlan}
            currentInterval={currentInterval}
            canManage={canManage}
            price={priceFor(tier.code)}
            priceLoading={plansQ.isLoading}
            fmt={fmt}
            pending={pending}
            companyId={companyId}
            onSubscribe={subscribe}
          />
        ))}
      </div>
    </>
  );
}

function TierCard({
  tier, interval, currentPlan, currentInterval, canManage, price, priceLoading, fmt, pending, companyId, onSubscribe,
}: {
  tier: { code: 'free' | 'growth' | 'global' | 'enterprise'; name: string; featureKeys: string[] };
  interval: 'monthly' | 'yearly';
  currentPlan: 'free' | 'growth' | 'global' | 'enterprise';
  currentInterval: 'monthly' | 'yearly' | null;
  canManage: boolean;
  price: number;
  priceLoading: boolean;
  fmt: (n: number) => string;
  pending: 'growth' | 'global' | 'enterprise' | null;
  companyId: string | null;
  onSubscribe: (plan: 'growth' | 'global' | 'enterprise', tierInterval: 'monthly' | 'yearly') => void;
}) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const isCurrentPlan = tier.code === currentPlan;
  // Disable ONLY the current plan+interval combo — switching cycle (or
  // tier) stays actionable. For Free there is no interval, so any "current
  // plan" Free is simply current.
  const isCurrentCombo = isCurrentPlan && (tier.code === 'free' || currentInterval === interval || currentInterval === null);

  const visible = expanded ? tier.featureKeys : tier.featureKeys.slice(0, FEATURE_PREVIEW_COUNT);
  const hidden = tier.featureKeys.length - FEATURE_PREVIEW_COUNT;

  const buttonLabel = isCurrentCombo
    ? t('settings.currentPlan', { defaultValue: 'Current plan' })
    : pending === tier.code
      ? t('settings.opening', { defaultValue: 'Opening…' })
      : canManage
        ? t('settings.switch', { defaultValue: 'Switch' })
        : t('settings.subscribe', { defaultValue: 'Subscribe' });

  return (
    <div
      className={
        'rounded-xl border p-3.5 flex flex-col ' +
        (isCurrentPlan
          ? 'border-accent-500 bg-accent-50/40 dark:bg-accent-950/20 ring-1 ring-accent-500/30'
          : 'border-ink-200 dark:border-ink-700 bg-white dark:bg-ink-900')
      }
    >
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold">{tier.name}</h3>
        {isCurrentPlan && <span className="text-[10px] uppercase tracking-wide font-bold text-accent-700 dark:text-accent-400">{t('billing.currentBadge', { defaultValue: 'Current' })}</span>}
      </div>
      <p className="mt-2 num text-2xl font-bold">
        {priceLoading ? '—' : fmt(price)}
      </p>
      <p className="text-xs text-ink-500">
        {tier.code === 'free'
          ? t('pricing.foreverFree', { defaultValue: 'Forever free' })
          : interval === 'monthly'
            ? t('pricing.perUserMonth', { defaultValue: '/ user / month' })
            : t('pricing.perUserMonthYearly', { defaultValue: '/ user / month, billed yearly' })}
      </p>
      <ul className="mt-3 space-y-1 text-[11.5px] text-ink-600 dark:text-ink-300 flex-1">
        {visible.map((k) => (
          <li key={k}>· {t(`pricing.feat.${k}`)}</li>
        ))}
      </ul>
      {hidden > 0 && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="mt-2 self-start text-[11px] font-semibold text-accent-700 dark:text-accent-400 hover:underline"
        >
          {expanded
            ? t('pricing.showLess', { defaultValue: 'Show less' })
            : t('pricing.showAll', { count: hidden, defaultValue: 'Show all features' })}
        </button>
      )}
      {tier.code !== 'free' && (
        <button
          type="button"
          disabled={isCurrentCombo || pending !== null || !companyId}
          onClick={() => onSubscribe(tier.code as 'growth' | 'global' | 'enterprise', interval)}
          className={
            'mt-3 w-full rounded-md px-3 py-2 text-xs font-semibold transition ' +
            (isCurrentCombo
              ? 'bg-ink-100 dark:bg-ink-800 text-ink-500 cursor-default'
              : 'bg-accent-600 text-white hover:bg-accent-700 disabled:opacity-60')
          }
        >
          {buttonLabel}
        </button>
      )}
    </div>
  );
}

function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-ink-500">{label}</span>
      <span className={mono ? 'font-mono' : ''}>{value}</span>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────
// JP display prefs — 元号 (Reiwa) + 全角 digits + furigana (spec §6.4)
// ──────────────────────────────────────────────────────────────────
function JpPrefsCard() {
  const { t } = useTranslation();
  const { data: user } = useCurrentUser();
  const qc = useQueryClient();
  const toast = useToast();

  const setPref = useMutation({
    mutationFn: async (patch: { useReiwa?: boolean; fullWidthDigits?: boolean; showFurigana?: boolean }) =>
      (await api.patch('/auth/me/prefs', patch)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['currentUser'] }),
    onError: () => toast.error(t('settings.savePrefsError')),
  });

  const prefs = user?.prefs ?? { useReiwa: false, fullWidthDigits: false, showFurigana: false };

  return (
    <Card className="mb-5">
      <CardHeader>
        <div className="font-medium">{t('settings.jpPrefs.title')}</div>
      </CardHeader>
      <CardBody className="space-y-3 text-sm">
        <Toggle
          label={t('settings.jpPrefs.reiwa')}
          hint={t('settings.jpPrefs.reiwaHint')}
          checked={prefs.useReiwa}
          onChange={(v) => setPref.mutate({ useReiwa: v })}
        />
        <Toggle
          label={t('settings.jpPrefs.zenkaku')}
          hint={t('settings.jpPrefs.zenkakuHint')}
          checked={prefs.fullWidthDigits}
          onChange={(v) => setPref.mutate({ fullWidthDigits: v })}
        />
        <Toggle
          label={t('settings.jpPrefs.furigana')}
          hint={t('settings.jpPrefs.furiganaHint')}
          checked={prefs.showFurigana}
          onChange={(v) => setPref.mutate({ showFurigana: v })}
        />
      </CardBody>
    </Card>
  );
}

function Toggle({ label, hint, checked, onChange }: { label: string; hint?: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="flex items-start gap-3 cursor-pointer">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-1 h-4 w-4 rounded border-ink-300 accent-accent-600"
      />
      <span className="min-w-0 flex-1">
        <span className="font-medium block">{label}</span>
        {hint && <span className="text-ink-500 text-xs leading-relaxed">{hint}</span>}
      </span>
    </label>
  );
}

